import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";

interface PageContentRow {
  section_key: string;
  content_en: string;
  content_fr: string;
  is_hidden: boolean;
}

export const usePageContent = (pageSlug: string) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith("fr") ? "fr" : "en";

  const { data: contentRows, isLoading } = useQuery({
    queryKey: ["page-content", pageSlug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_content")
        .select("section_key, content_en, content_fr, is_hidden")
        .eq("page_slug", pageSlug);
      
      if (error) throw error;
      return data as PageContentRow[];
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  // Build a lookup map for quick access
  const contentMap = new Map<string, { en: string; fr: string; is_hidden: boolean }>();
  contentRows?.forEach((row) => {
    contentMap.set(row.section_key, {
      en: row.content_en,
      fr: row.content_fr,
      is_hidden: row.is_hidden,
    });
  });

  // Helper function to get content with fallback
  const getContent = (sectionKey: string, fallback: string): string => {
    const content = contentMap.get(sectionKey);
    if (content) {
      const value = currentLang === "fr" ? content.fr : content.en;
      // Return the value if it's not empty, otherwise use fallback
      if (value && value.trim()) {
        return value;
      }
    }
    return fallback;
  };

  // Helper function to check if a section is hidden
  const isSectionHidden = (sectionKey: string): boolean => {
    const content = contentMap.get(sectionKey);
    return content?.is_hidden ?? false;
  };

  return {
    getContent,
    isSectionHidden,
    isLoading,
    hasContent: contentRows && contentRows.length > 0,
  };
};

export default usePageContent;
